import {
  Box,
  Button,
  Chip,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  makeStyles
} from '@material-ui/core'
import AddIcon from '@material-ui/icons/Add'
import EditOutlinedIcon from '@material-ui/icons/EditOutlined'
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined'
import PropTypes from 'prop-types'
import React from 'react'

import { totalPermissions } from 'app/permissions'
import { colors } from 'app/theme/colors'

const useStyles = makeStyles((theme) => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2)
  },
  subtitle: { color: colors.textSecondary },
  roleName: { fontWeight: 600 },
  description: { color: colors.textSecondary },
  actions: { whiteSpace: 'nowrap' },
  empty: {
    padding: theme.spacing(4),
    textAlign: 'center',
    color: colors.textTertiary
  }
}))

// No delete action, deliberately: a role can never be removed — not from Alfred,
// and not from the database either (User.role is PROTECT, and the Postgres FK is
// NO ACTION, so even raw SQL cannot drop a role somebody holds).
const Roles = ({
  roles,
  userCountByRole,
  onCreate,
  onEdit,
  onDuplicate,
  onViewPeople
}) => {
  const classes = useStyles()

  return (
    <Box>
      <Box className={classes.header}>
        <Box>
          <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
            Roles
          </Typography>
          <Typography variant="body2" className={classes.subtitle}>
            A role is a set of Alfred tabs. It controls what a person sees in
            the sidebar — assign it from the Admin and Teacher screens under
            Users.
          </Typography>
        </Box>
        <Button
          variant="contained"
          color="primary"
          disableElevation
          startIcon={<AddIcon />}
          onClick={onCreate}
        >
          New role
        </Button>
      </Box>

      <Paper variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Role</TableCell>
              <TableCell>Permissions</TableCell>
              <TableCell>People</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {roles.map((role) => {
              const userCount =
                role.users_count ?? userCountByRole[role.uuid] ?? 0
              return (
                <TableRow key={role.uuid} hover>
                  <TableCell>
                    <Typography variant="body2" className={classes.roleName}>
                      {role.name}
                      {role.is_super_admin && (
                        <Chip
                          size="small"
                          color="primary"
                          label="Super Admin"
                          style={{ marginLeft: 8 }}
                        />
                      )}
                    </Typography>
                    <Typography
                      variant="caption"
                      className={classes.description}
                    >
                      {role.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      variant="outlined"
                      label={
                        role.is_super_admin
                          ? 'Everything, always'
                          : `${
                              (role.permissions || []).length
                            } of ${totalPermissions} tabs`
                      }
                    />
                  </TableCell>
                  <TableCell>
                    {userCount ? (
                      <Link
                        component="button"
                        variant="body2"
                        onClick={() => onViewPeople(role.uuid)}
                      >
                        {userCount} {userCount === 1 ? 'person' : 'people'}
                      </Link>
                    ) : (
                      <Typography
                        variant="body2"
                        className={classes.description}
                      >
                        None
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right" className={classes.actions}>
                    <Tooltip title="Edit role">
                      <IconButton size="small" onClick={() => onEdit(role)}>
                        <EditOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Duplicate role">
                      <IconButton
                        size="small"
                        onClick={() => onDuplicate(role)}
                      >
                        <FileCopyOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              )
            })}
            {!roles.length && (
              <TableRow>
                <TableCell colSpan={4} className={classes.empty}>
                  No roles yet. Create one to get started.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  )
}

Roles.propTypes = {
  roles: PropTypes.array.isRequired,
  userCountByRole: PropTypes.object.isRequired,
  onCreate: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDuplicate: PropTypes.func.isRequired,
  onViewPeople: PropTypes.func.isRequired
}

export default Roles
